import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row } from 'react-table';
import styled from 'styled-components';

import { Button, Chip, Icon, Tooltip, formatNumber } from '@column/column-ui-kit';

import { TransferAmount } from '~/app/pages/Transfers/Overview';
import { PageHeader, Pagination, Table, TableColumn } from '~/components';
import { RelativeTime } from '~/elements';
import { useReviewAchTransfer, useReviewWireTransfer } from '~/hooks/useTransfers';
import { useNavigate } from '~/lib/navigation';
import { formatTransferType, getTransferIcon, getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType, TransferRepository, convertTransferAmount } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { TableBottomFade } from '~/styles';
import { UnifiedTransfer } from '~/typings/API';
import { ReviewTransferRequest } from '~/typings/enum';

import { TransferReviewModal } from './_components/TransferReviewModal';

interface ApprovalsTableRow {
  id: string;
  createdAt: string;
  description: [React.ReactNode, string];
  transferType: TransferEntryType;
  viewMoreUrl: string;
}

const Note = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

const Description = styled.div`
  display: flex;
  align-items: baseline;
  grid-gap: 8px;
  padding: 4px 0;
  max-width: 100%;

  ${Note} {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const StyledTableBottomFade = styled(TableBottomFade)`
  display: flex;
  justify-content: flex-end;
`;

const buildRow = ({
  amount,
  createdAt,
  currencyCode,
  description,
  id,
  type: transferType,
}: UnifiedTransfer): ApprovalsTableRow => {
  const isIncoming = false;
  const convertAmount = convertTransferAmount(transferType, isIncoming);

  return {
    id,
    createdAt,
    viewMoreUrl: getTransferURL(id, transferType),
    transferType,
    description: [
      <TransferAmount key={id} $isAdding={convertAmount !== -1}>
        {formatNumber(amount ?? 0, currencyCode)}
      </TransferAmount>,
      description,
    ],
  };
};

const ITEMS_PER_PAGE = 10;

export const PageTransferApprovalsOverview: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const { currentPlatform } = useSessionStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: transfersData,
    isLoading: isTransfersLoading,
    isFetchingNextPage: isTransfersFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['transfers', 'pre_review', currentPlatform],
    queryFn: async ({ pageParam }) =>
      TransferRepository.getAll({
        status: 'pre_review' as any,
        limit: ITEMS_PER_PAGE,
        startingAfter: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore && lastPage.transfers.length > 0) {
        return lastPage.transfers[lastPage.transfers.length - 1].id;
      }
      return undefined;
    },
    initialPageParam: undefined as string | undefined,
  });
  const allTransfers = useMemo(() => {
    return transfersData?.pages.flatMap((page) => page.transfers) ?? [];
  }, [transfersData?.pages]);
  const [currentPage, setCurrentPage] = useState(0);
  const totalResultsCount = transfersData?.pages[0]?.totalResultsCount ?? 0;
  const totalPages = Math.ceil(totalResultsCount / ITEMS_PER_PAGE);
  const handleTablePrevious = useCallback(() => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);
  const handleTableNext = useCallback(() => {
    if (currentPage >= (transfersData?.pages?.length ?? 0) - 1 && hasNextPage) {
      fetchNextPage();
    }
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : prev));
  }, [currentPage, totalPages, hasNextPage, fetchNextPage, transfersData?.pages?.length]);

  const { createRequest: createAchReviewReq } = useReviewAchTransfer({
    onError: (err) => {
      addDangerNotification({
        content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
      });
    },
  });
  const { createRequest: createWireReviewReq } = useReviewWireTransfer({
    onError: (err) => {
      addDangerNotification({
        content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
      });
    },
  });
  const reviewTransfer = useCallback(
    async (id: string, transferType: TransferEntryType, req: ReviewTransferRequest) => {
      if (transferType === 'ach_credit' || transferType === 'ach_debit') {
        await createAchReviewReq({ id, ...req });
      } else {
        await createWireReviewReq({ id, ...req });
      }
    },
    [createAchReviewReq, createWireReviewReq]
  );

  const columns: TableColumn<ApprovalsTableRow>[] = useMemo(() => {
    return [
      {
        Header: 'Amount',
        accessor: 'description',
        width: 'minmax(80px, auto)',
        Cell: (current) => (
          <Description>
            {current.value[0]}
            {current.value[1] && <Note>{current.value[1]}</Note>}
          </Description>
        ),
      },
      {
        Header: 'Type',
        accessor: 'transferType',
        width: 'minmax(min-content, 100px)',
        Cell: (current) => (
          <Chip variant="subtle" icon={getTransferIcon(current.value)}>
            {formatTransferType(current.value)}
          </Chip>
        ),
      },
      {
        Header: 'Created',
        accessor: 'createdAt',
        sortType: 'datetime',
        width: 'minmax(min-content, 70px)',
        Cell: (current) => <RelativeTime timestamp={current.value} />,
      },
      {
        Header: '',
        accessor: 'viewMoreUrl',
        width: 'min-content',
        Cell: (current) => (
          <Button
            icon={<Icon.ChevronRightLarge />}
            iconRight
            onClick={(event: React.SyntheticEvent) => {
              event.stopPropagation();
              navigate(current.value);
            }}
            size="tiny"
            variant="secondary"
          >
            View Transfer
          </Button>
        ),
      },
    ];
  }, []);

  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [currentModalIndex, setCurrentModalIndex] = useState<number>(0);
  const handleModalNext = useCallback(() => {
    const nextIndex = currentModalIndex + 1;
    if (nextIndex >= allTransfers.length && hasNextPage) {
      fetchNextPage();
    }
    setCurrentModalIndex(nextIndex);
  }, [currentModalIndex, allTransfers.length, hasNextPage, fetchNextPage]);
  const handleModalPrevious = useCallback(() => {
    const prevIndex = currentModalIndex - 1;
    if (prevIndex < 0) {
      return;
    }
    setCurrentModalIndex(prevIndex);
  }, [currentModalIndex]);

  // Ensure currentModalIndex stays within valid bounds when data changes
  useEffect(() => {
    if (currentModalIndex >= totalResultsCount && totalResultsCount > 0) {
      setCurrentModalIndex(totalResultsCount - 1);
    }
  }, [currentModalIndex, totalResultsCount]);

  const tableData = useMemo(() => {
    return transfersData?.pages[currentPage]?.transfers.map((transfer) => buildRow(transfer)) ?? [];
  }, [transfersData?.pages, currentPage]);

  const onRowClick = useCallback(
    (row: Row<ApprovalsTableRow>) => {
      setIsApprovalModalOpen(true);
      setCurrentModalIndex(allTransfers.findIndex((transfer) => transfer.id === row.original.id));
    },
    [allTransfers]
  );

  const handleModalClose = useCallback(() => {
    setIsApprovalModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['transfers', 'pre_review', currentPlatform] });
  }, [queryClient, currentPlatform]);

  return (
    <>
      <TransferReviewModal
        currentIndex={currentModalIndex}
        transfers={allTransfers}
        totalTransfers={totalResultsCount}
        isOpen={isApprovalModalOpen}
        onClose={handleModalClose}
        onNextTransfer={handleModalNext}
        onPreviousTransfer={handleModalPrevious}
        onSubmit={async (transferId, transferType, request) => {
          await reviewTransfer(transferId, transferType, request);
          queryClient.setQueryData(['transfers', 'pre_review', currentPlatform], (oldData: any) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page: any) => ({
                ...page,
                transfers: page.transfers.filter((transfer: UnifiedTransfer) => transfer.id !== transferId),
                totalResultsCount: page.totalResultsCount - 1,
              })),
            };
          });
        }}
      />
      <PageHeader text="Pending Approvals" borderOnScroll>
        <Tooltip
          content="No transfers to review"
          isOpen={allTransfers.length === 0 ? undefined : false}
          placement="bottom"
        >
          <Button
            isDisabled={allTransfers.length === 0}
            onClick={() => {
              setIsApprovalModalOpen(true);
              setCurrentModalIndex(0);
            }}
            size="small"
          >
            Review Transfers
          </Button>
        </Tooltip>
      </PageHeader>
      <Table
        columns={columns}
        data={tableData}
        isLoading={(isTransfersFetchingNextPage || isTransfersLoading) && tableData.length === 0}
        onRowClick={onRowClick}
      />
      <StyledTableBottomFade show={!isTransfersLoading || allTransfers.length > 0} timeoutEnter={400}>
        <Pagination
          disablePrev={currentPage === 0}
          disableNext={currentPage >= totalPages - 1}
          current={currentPage + 1}
          onPrev={handleTablePrevious}
          onNext={handleTableNext}
        />
      </StyledTableBottomFade>
    </>
  );
};
